import React, { useState, useEffect, Suspense, useCallback } from 'react'
import { Link, useLocation, Outlet } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import {
  TrendingUp,
  BarChart3,
  Activity,
  LogOut,
  Settings,
  User,
  Menu,
  X,
  CandlestickChart,
  Sparkles,
  Sun,
  Moon,
  Building2,
  RefreshCcw,
  Wallet,
  Shield,
  Scale,
  BookOpen,
  Search,
  Eye,
  Layers,
  ShoppingBag,
  Code,
  MessageCircle,
  Clock,
  Calculator,
  Terminal,
  GitCompare,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  SlidersHorizontal,
  LayoutGrid,
  Plug,
  BookTemplate,
  Webhook,
  Radio,
  AlertTriangle,
  Gauge,
  Zap,
} from 'lucide-react'

import Breadcrumbs from '@components/ui/Breadcrumbs'
import ProductReleaseNotice from '@components/layout/ProductReleaseNotice'
import { useAuth } from '@contexts/AuthContext'
import { useConfig } from '@contexts/ConfigContext'
import { useTheme } from '@contexts/ThemeContext'
import Button from '@components/ui/Button'
import ContentSkeleton from '@components/ui/ContentSkeleton'
import NotificationCenter from '@components/ui/NotificationCenter'
import PnLTicker from '@components/ui/PnLTicker'
import WsHealthIndicator from '@components/ui/WsHealthIndicator'
import RunningBacktests from '@components/backtests/RunningBacktests'
import { useReducedMotion } from '@hooks/useReducedMotion'
import {
  useCurrentPageTitle,
  useCurrentPageDescription,
} from '@contexts/PageTitleContext'
import { api as _api } from '@services/enhancedApiService'
import { brokerConnectionsService } from '@services/brokerConnectionsService'
import { DASHBOARD_FEATURES } from '@utils/featureFlags'

/**
 * Build navigation groups based on user role.
 * Reorders groups: ungrouped → AI & Signals → Trading → Analysis → Tools → System → (Admin for admin/superAdmin)
 * For admin/superAdmin: adds the dedicated Platform Console entry.
   * Trader-facing destinations remain in their normal groups for every role.
 * @param {string} role - User role: 'trader', 'admin', or 'superAdmin'
 * @returns {Array} Navigation groups array
 */
export function buildNavGroups(role) {
  const isAdmin = role === 'admin' || role === 'superAdmin'

  const analysisItems = [
    { name: 'Algo Compare', href: '/algo-compare', icon: Scale },
    { name: 'Backtest Compare', href: '/backtest-compare', icon: GitCompare },
    { name: 'Correlation', href: '/correlation', icon: Activity },
    {
      name: 'Screener',
      href: '/screener',
      icon: Search,
      feature: DASHBOARD_FEATURES.screener,
    },
    {
      name: 'OI Analysis',
      href: '/oi-analysis',
      icon: Eye,
      feature: DASHBOARD_FEATURES.oiAnalysis,
    },
    {
      name: 'Options Builder',
      href: '/options',
      icon: Layers,
      feature: DASHBOARD_FEATURES.optionsBuilder,
    },
    {
      name: 'Options Analytics',
      href: '/options-analytics',
      icon: Zap,
      feature: DASHBOARD_FEATURES.optionsAnalytics,
    },
  ]

  const systemItems = [
    { name: 'Activity', href: '/activity', icon: Clock },
    {
      name: 'System Status',
      href: '/status',
      icon: Activity,
      feature: DASHBOARD_FEATURES.statusPage,
    },
    { name: 'SEBI Compliance', href: '/compliance', icon: Shield },
    { name: 'Settings', href: '/settings', icon: Settings },
  ]

  const groups = [
    {
      label: null,
      items: [
        { name: 'Dashboard', href: '/', icon: BarChart3 },
        {
          name: 'Custom Dashboard',
          href: '/custom-dashboard',
          icon: LayoutGrid,
        },
      ],
    },
    {
      label: 'AI & Signals',
      items: [
        { name: 'x3 Assistant', href: '/assistant', icon: MessageCircle },
        { name: 'Market Monitor', href: '/market-monitor', icon: Sparkles },
        { name: 'AI Usage', href: '/ai-usage', icon: Gauge },
      ],
    },
    {
      label: 'Trading',
      items: [
        { name: 'Algorithms', href: '/algorithms', icon: TrendingUp },
        {
          name: 'Strategy Templates',
          href: '/strategy-templates',
          icon: BookTemplate,
        },
        { name: 'Charts', href: '/charts', icon: CandlestickChart },
        { name: 'Brokers', href: '/brokers', icon: Building2 },
        { name: 'Trading History', href: '/trading-history', icon: Activity },
        { name: 'Paper Trading', href: '/paper-trading', icon: Wallet },
        { name: 'Portfolio', href: '/portfolio', icon: Layers },
        {
          name: 'Contract Rollover',
          href: '/contract-rollover',
          icon: RefreshCcw,
        },
        { name: 'Arbitrage', href: '/arbitrage', icon: Scale },
        {
          name: 'Conditional Orders',
          href: '/conditional-orders',
          icon: SlidersHorizontal,
        },
      ],
    },
    {
      label: 'Analysis',
      items: analysisItems,
    },
    {
      label: 'Tools',
      items: [
        { name: 'Trade Journal', href: '/journal', icon: BookOpen },
        { name: 'Code Editor', href: '/code-editor', icon: Code },
        {
          name: 'Marketplace',
          href: '/marketplace',
          icon: ShoppingBag,
          feature: DASHBOARD_FEATURES.marketplace,
        },
        {
          name: 'API Playground',
          href: '/api-playground',
          icon: Terminal,
          feature: DASHBOARD_FEATURES.apiPlayground,
        },
        {
          name: 'x3 Connect',
          href: '/x3-connect',
          icon: Plug,
          feature: DASHBOARD_FEATURES.mcpManagement,
        },
        { name: 'Webhooks', href: '/webhooks', icon: Webhook },
        { name: 'TradingView', href: '/tradingview-webhooks', icon: Radio },
        { name: 'Calculators', href: '/calculators', icon: Calculator },
      ],
    },
    {
      label: 'System',
      items: systemItems,
    },
  ]

  if (isAdmin) {
    groups.push({
      label: 'Admin',
      items: [{ name: 'Platform Console', href: '/admin', icon: Shield }]
    })
  }

  return groups
}

export function filterNavGroupsByFeatures(groups, isFeatureEnabled) {
  return groups
    .map((group) => ({
      ...group,
      items: group.items.filter(
        (item) => !item.feature || isFeatureEnabled(item.feature),
      ),
    }))
    .filter((group) => group.items.length > 0)
}

function getInitialCollapsed() {
  try {
    const saved = localStorage.getItem('sidebarCollapsed')
    return saved ? JSON.parse(saved) : {}
  } catch {
    return {}
  }
}

function getInitialMiniCollapsed() {
  try {
    const saved = localStorage.getItem('sidebarMiniCollapsed')
    return saved === 'true'
  } catch {
    return false
  }
}

/**
 * Mini sidebar flyout group — uses fixed positioning so the flyout
 * escapes the scrollable sidebar container without being clipped.
 */
function MiniNavGroup({
  group,
  idx,
  showDivider,
  GroupIcon,
  isActive,
  onClick,
  renderNavLink,
}) {
  const triggerRef = React.useRef(null)
  const [flyoutTop, setFlyoutTop] = useState(null)
  const [showFlyout, setShowFlyout] = useState(false)

  const handleMouseEnter = useCallback(() => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect()
      setFlyoutTop(rect.top)
    }
    setShowFlyout(true)
  }, [])

  const handleMouseLeave = useCallback(() => {
    setShowFlyout(false)
  }, [])

  const handleFlyoutClick = useCallback(() => {
    setShowFlyout(false)
    onClick()
  }, [onClick])

  return (
    <div
      key={group.label}
      className={`relative ${idx > 0 ? 'mt-3' : ''}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {showDivider && (
        <hr className="border-gray-200 dark:border-gray-700 mb-3" />
      )}
      <div
        ref={triggerRef}
        className="flex items-center justify-center p-2 text-gray-400 dark:text-gray-500 cursor-default"
        aria-label={group.label}
      >
        <GroupIcon className="w-4 h-4" aria-hidden="true" />
      </div>
      {/* Flyout panel — fixed positioning to escape scroll container */}
      {showFlyout && flyoutTop !== null && (
        <div
          className="fixed left-16 ml-1 py-2 px-3 min-w-[180px] rounded-lg bg-white dark:bg-gray-800 shadow-lg border border-gray-200 dark:border-gray-700 z-50"
          style={{ top: flyoutTop }}
          role="menu"
          aria-label={`${group.label} menu`}
        >
          <div className="text-[11px] font-semibold uppercase tracking-[0.05em] text-gray-400 dark:text-gray-500 mb-1 px-2">
            {group.label}
          </div>
          <ul className="space-y-0.5">
            {group.items.map((item) => {
              const FlyoutIcon = item.icon
              const active = isActive(item.href)
              return (
                <li key={item.name}>
                  <Link
                    to={item.href}
                    onClick={handleFlyoutClick}
                    aria-current={active ? 'page' : undefined}
                    className={`flex items-center px-2 py-1.5 rounded text-sm ${active ? 'text-primary-600 dark:text-primary-400 font-medium bg-primary-50 dark:bg-primary-900/20' : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'} transition-colors`}
                  >
                    <FlyoutIcon
                      className="w-4 h-4 mr-2 flex-shrink-0"
                      aria-hidden="true"
                    />
                    {item.name}
                  </Link>
                </li>
              )
            })}
          </ul>
        </div>
      )}
      <ul className="space-y-1 mt-1">
        {group.items.map((item) =>
          renderNavLink(item, handleFlyoutClick, true),
        )}
      </ul>
    </div>
  )
}

/**
 * Dashboard Layout Component
 * Provides the main layout structure for all dashboard pages
 * WCAG 2.1 AA compliant: skip-nav, landmarks, aria-live, focus management
 */
function DashboardLayout() {
  const { user, logout } = useAuth()
  const { isFeatureEnabled } = useConfig()
  const { theme: _theme, toggleTheme, isDark } = useTheme()
  const location = useLocation()
  const isAssistantWorkspace = ['/assistant', '/ai-chat'].includes(location.pathname)
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [collapsed, setCollapsed] = useState(getInitialCollapsed)
  const [miniCollapsed, setMiniCollapsed] = useState(getInitialMiniCollapsed)
  // Focus mode: hides dashboard chrome for immersive AI assistant experience
  const [focusMode, setFocusMode] = useState(() => {
    if (!isAssistantWorkspace) return false
    try {
      return localStorage.getItem('assistantFocusMode') === 'true'
    } catch {
      return false
    }
  })
  const prefersReducedMotion = useReducedMotion()
  const title = useCurrentPageTitle()
  const description = useCurrentPageDescription()
  const [unreadCount, setUnreadCount] = useState(0)

  // Sync focus mode with localStorage and reset when leaving assistant
  useEffect(() => {
    if (!isAssistantWorkspace && focusMode) {
      setFocusMode(false)
    }
  }, [isAssistantWorkspace, focusMode])

  const toggleFocusMode = useCallback(() => {
    setFocusMode(prev => {
      const next = !prev
      try {
        localStorage.setItem('assistantFocusMode', String(next))
      } catch { /* ignore */ }
      return next
    })
  }, [])

  // Expose focus mode toggle to child components via window (simple approach)
  useEffect(() => {
    if (isAssistantWorkspace) {
      window.__x3_toggleFocusMode = toggleFocusMode
      window.__x3_focusMode = focusMode
      return () => {
        delete window.__x3_toggleFocusMode
        delete window.__x3_focusMode
      }
    }
  }, [isAssistantWorkspace, focusMode, toggleFocusMode])

  // Close mobile sidebar on Escape key
  useEffect(() => {
    if (!sidebarOpen) return
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') setSidebarOpen(false)
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [sidebarOpen])

  const handleLogout = async () => {
    await logout()
  }

  const toggleGroup = useCallback((label) => {
    setCollapsed((prev) => {
      const next = { ...prev, [label]: !prev[label] }
      localStorage.setItem('sidebarCollapsed', JSON.stringify(next))
      return next
    })
  }, [])

  const toggleMiniCollapse = useCallback(() => {
    setMiniCollapsed((prev) => {
      const next = !prev
      try {
        localStorage.setItem('sidebarMiniCollapsed', String(next))
      } catch {
        // Silent failure if localStorage unavailable
      }
      return next
    })
  }, [])

  const isActive = (href) => {
    if (href === '/') return location.pathname === '/'
    // Use exact match for routes that are prefixes of other routes
    if (href === '/options') return location.pathname === '/options' || location.pathname.startsWith('/options/')
    return location.pathname.startsWith(href)
  }

  const renderNavLink = (item, onClick, isMini = false) => {
    const Icon = item.icon
    const active = isActive(item.href)
    return (
      <li key={item.name} className={isMini ? 'relative group/tooltip' : ''}>
        <Link
          to={item.href}
          onClick={onClick}
          aria-current={active ? 'page' : undefined}
          className={`sidebar-link ${active ? 'sidebar-link-active border-l-4 border-primary-500' : 'sidebar-link-inactive'} ${isMini ? 'justify-center px-0' : ''}`}
        >
          <Icon
            className={`w-5 h-5 ${isMini ? '' : 'mr-3'} flex-shrink-0`}
            aria-hidden="true"
          />
          {!isMini && (
            <>
              {active ? (
                <span className="text-gradient-animated">{item.name}</span>
              ) : (
                item.name
              )}
              {item.name === 'Activity' && unreadCount > 0 && (
                <span className="ml-auto inline-flex items-center justify-center px-1.5 py-0.5 text-[10px] font-bold leading-none text-white bg-red-500 rounded-full min-w-[18px]">
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
            </>
          )}
        </Link>
        {isMini && (
          <div
            role="tooltip"
            className="pointer-events-none absolute left-full top-1/2 -translate-y-1/2 ml-2 px-2 py-1 rounded bg-gray-900 dark:bg-gray-700 text-white text-xs whitespace-nowrap opacity-0 group-hover/tooltip:opacity-100 transition-opacity z-50"
          >
            {item.name}
          </div>
        )}
      </li>
    )
  }

  const renderGroupedNav = (onClick, isMini = false) => {
    const groups = filterNavGroupsByFeatures(
      buildNavGroups(user?.role || 'trader'),
      isFeatureEnabled,
    )
    const labeledGroups = groups.filter((g) => g.label)

    return groups.map((group, idx) => {
      if (!group.label) {
        return (
          <ul key="ungrouped" className="space-y-1">
            {group.items.map((item) => renderNavLink(item, onClick, isMini))}
          </ul>
        )
      }

      const hasActiveItem = group.items.some((item) => isActive(item.href))
      const isCollapsed = hasActiveItem ? false : collapsed[group.label]
      const labeledIndex = labeledGroups.indexOf(group)
      const showDivider = labeledIndex > 0

      if (isMini) {
        const GroupIcon = group.items[0]?.icon || ChevronDown
        return (
          <MiniNavGroup
            key={group.label}
            group={group}
            idx={idx}
            showDivider={showDivider}
            GroupIcon={GroupIcon}
            isActive={isActive}
            onClick={onClick}
            renderNavLink={renderNavLink}
          />
        )
      }

      return (
        <div key={group.label} className={idx > 0 ? 'mt-3' : ''}>
          {showDivider && (
            <hr className="border-gray-200 dark:border-gray-700 mb-3" />
          )}
          <button
            onClick={() => toggleGroup(group.label)}
            aria-expanded={!isCollapsed}
            className="w-full flex items-center justify-between px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.05em] text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
          >
            <span>{group.label}</span>
            <ChevronDown
              className={`w-3.5 h-3.5 transition-transform ${isCollapsed ? '-rotate-90' : ''}`}
              aria-hidden="true"
            />
          </button>
          {!isCollapsed && (
            <ul className="space-y-1 mt-1">
              {group.items.map((item) => renderNavLink(item, onClick, isMini))}
            </ul>
          )}
        </div>
      )
    })
  }

  return (
    <div className={`${isAssistantWorkspace ? 'h-[100dvh]' : 'h-screen'} flex flex-col bg-gray-50 dark:bg-gray-900 overflow-hidden`}>
      {/* Skip to main content link — WCAG 2.4.1 */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:z-50 focus:top-2 focus:left-2 focus:px-4 focus:py-2 focus:bg-primary-600 focus:text-white focus:rounded focus:outline-none"
      >
        Skip to main content
      </a>

      {/* Navigation Header - hidden in focus mode */}
      {!(isAssistantWorkspace && focusMode) && (
        <header
          className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700 relative z-30"
          role="banner"
        >
        <div className="container">
          <div className="flex items-center justify-between h-16">
            {/* Logo and Mobile Menu Button */}
            <div className="flex items-center space-x-4">
              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                aria-expanded={sidebarOpen}
                aria-controls="mobile-sidebar"
                aria-label={
                  sidebarOpen ? 'Close navigation menu' : 'Open navigation menu'
                }
                className="md:hidden p-2 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                {sidebarOpen ? (
                  <X className="w-6 h-6" aria-hidden="true" />
                ) : (
                  <Menu className="w-6 h-6" aria-hidden="true" />
                )}
              </button>

              <Link
                to="/"
                className="flex items-center space-x-2"
                aria-label="x3Algo Trading Dashboard — Home"
              >
                <img
                  src="/assets/brand/x3algo-mark.svg"
                  alt=""
                  className="h-8 w-8 shrink-0"
                  aria-hidden="true"
                />
                <span className="text-xl font-bold tracking-normal text-gray-900 dark:text-white">
                  x3
                  <span className="text-primary-600 dark:text-primary-400">
                    Algo
                  </span>
                </span>
                <span className="ml-2 hidden text-sm text-gray-500 dark:text-gray-400 sm:block">
                  Trading Dashboard
                </span>
              </Link>
            </div>

            {/* User Menu */}
            <nav aria-label="User actions">
              <div className="flex items-center space-x-4">
                <div className="text-sm text-gray-600 dark:text-gray-300 hidden sm:block">
                  Welcome,{' '}
                  <span className="font-medium">{user?.name || 'Trader'}</span>!
                </div>

                <div className="flex items-center space-x-2">
                  {/* Real-time P&L Ticker */}
                  <PnLTicker />

                  {/* Running Backtests Indicator */}
                  <RunningBacktests />

                  {/* WebSocket Health */}
                  <WsHealthIndicator />

                  {/* Notification Center */}
                  <NotificationCenter onUnreadCountChange={setUnreadCount} />

                  {/* Theme Toggle */}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={toggleTheme}
                    aria-label={
                      isDark ? 'Switch to light mode' : 'Switch to dark mode'
                    }
                  >
                    {isDark ? (
                      <Sun className="w-4 h-4" aria-hidden="true" />
                    ) : (
                      <Moon className="w-4 h-4" aria-hidden="true" />
                    )}
                  </Button>

                  <Link to="/profile">
                    <Button variant="ghost" size="sm" aria-label="View profile">
                      <User className="w-4 h-4 mr-2" aria-hidden="true" />
                      <span className="hidden sm:inline">Profile</span>
                    </Button>
                  </Link>

                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleLogout}
                    aria-label="Log out"
                  >
                    <LogOut className="w-4 h-4 mr-2" aria-hidden="true" />
                    <span className="hidden sm:inline">Logout</span>
                  </Button>
                </div>
              </div>
            </nav>
          </div>
        </div>
      </header>
      )}

      <div className="flex flex-1 overflow-hidden">
        {/* Desktop Sidebar - hidden in focus mode */}
        {!(isAssistantWorkspace && focusMode) && (
          <nav
            className={`hidden md:flex flex-col ${miniCollapsed ? 'w-16' : 'w-64'} bg-white dark:bg-gray-800 shadow-lg border-r border-gray-200 dark:border-gray-700 overflow-hidden transition-[width] duration-200`}
            aria-label="Main navigation"
          >
          <div
            className={`flex-1 ${miniCollapsed ? 'p-2 overflow-y-auto scrollbar-hide' : 'p-4 overflow-y-auto scrollbar-thin'} space-y-1`}
          >
            {renderGroupedNav(() => setSidebarOpen(false), miniCollapsed)}
          </div>
          <div
            className={`border-t border-gray-200 dark:border-gray-700 ${miniCollapsed ? 'p-2' : 'p-2 px-4'}`}
          >
            <button
              onClick={toggleMiniCollapse}
              aria-label={miniCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              className="w-full flex items-center justify-center p-2 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              {miniCollapsed ? (
                <ChevronRight className="w-4 h-4" aria-hidden="true" />
              ) : (
                <ChevronLeft className="w-4 h-4" aria-hidden="true" />
              )}
            </button>
          </div>
        </nav>
        )}

        {/* Mobile Sidebar Overlay with Framer Motion animations - hidden in focus mode */}
        {!(isAssistantWorkspace && focusMode) && (
        <AnimatePresence>
          {sidebarOpen && (
            <>
              {/* Backdrop - fade in/out */}
              <motion.div
                key="sidebar-backdrop"
                className="md:hidden fixed inset-0 bg-black/50 z-10 pt-16"
                onClick={() => setSidebarOpen(false)}
                aria-hidden="true"
                initial={prefersReducedMotion ? { opacity: 1 } : { opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={prefersReducedMotion ? { opacity: 0 } : { opacity: 0 }}
                transition={
                  prefersReducedMotion
                    ? { duration: 0 }
                    : { duration: 0.25, ease: 'easeInOut' }
                }
              />

              {/* Slide panel - slide in from left */}
              <motion.nav
                key="sidebar-panel"
                id="mobile-sidebar"
                className="md:hidden fixed inset-y-0 left-0 z-20 w-64 bg-white dark:bg-gray-800 shadow-lg border-r border-gray-200 dark:border-gray-700 pt-16 overflow-y-auto scrollbar-thin"
                aria-label="Main navigation (mobile)"
                initial={prefersReducedMotion ? { x: 0 } : { x: '-100%' }}
                animate={{ x: 0 }}
                exit={prefersReducedMotion ? { x: 0 } : { x: '-100%' }}
                transition={
                  prefersReducedMotion
                    ? { duration: 0 }
                    : { duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }
                }
              >
                <div className="p-4 space-y-1">
                  {renderGroupedNav(() => setSidebarOpen(false))}
                </div>
              </motion.nav>
            </>
          )}
        </AnimatePresence>
        )}

        {/* Main Content */}
        <main
          id="main-content"
          className={isAssistantWorkspace
            ? 'flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden'
            : 'flex-1 overflow-y-auto scrollbar-thin'}
          role="main"
          tabIndex={-1}
        >
          {/* Hide banners and page header in focus mode */}
          {!(isAssistantWorkspace && focusMode) && (
            <>
              <ProductReleaseNotice />

              {/* Email verification banner for active but unverified users */}
              {user && !user.isVerified && <EmailVerificationBanner />}

              {/* Broker expiry banner — shown whenever any connected broker session has expired */}
              {user?.isVerified && <BrokerExpiryBanner />}

              {title && (
                <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                  <div className="container py-6">
                    <Breadcrumbs />
                    <h1 className="text-2xl font-bold text-gradient-animated">
                      {title}
                    </h1>
                    {description && (
                      <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                        {description}
                      </p>
                    )}
                  </div>
                </div>
              )}
            </>
          )}

          <div className={isAssistantWorkspace
            ? 'flex min-h-0 min-w-0 flex-1 flex-col'
            : 'container py-8'}>
            <Suspense fallback={<ContentSkeleton />}>
              <Outlet context={{ focusMode, toggleFocusMode }} />
            </Suspense>
          </div>
        </main>
      </div>

      {/* Live region for dynamic announcements — WCAG 4.1.3 */}
      <div
        aria-live="polite"
        aria-atomic="true"
        className="sr-only"
        id="live-announcements"
      />
    </div>
  )
}

function BrokerExpiryBanner() {
  const [expiredBrokers, setExpiredBrokers] = useState([])
  const [dismissed, setDismissed] = useState(false)

  useEffect(() => {
    let cancelled = false

    const check = async () => {
      try {
        const res = await brokerConnectionsService.getConnections()
        const data = res.data?.data ?? res.data ?? []
        if (!cancelled) {
          setExpiredBrokers(
            Array.isArray(data)
              ? data.filter(c =>
                  c.status === 'expired' ||
                  c.status === 'error' ||
                  // status may still be 'active' if the session refresh worker
                  // hasn't run yet — use the computed isExpired flag from toSafeObject
                  c.session?.isExpired === true
                )
              : []
          )
        }
      } catch {
        // non-critical — fail silently
      }
    }

    check()
    // Re-check every 5 minutes
    const id = setInterval(check, 5 * 60 * 1000)
    return () => { cancelled = true; clearInterval(id) }
  }, [])

  if (dismissed || expiredBrokers.length === 0) return null

  const names = expiredBrokers
    .map(c => c.broker.charAt(0).toUpperCase() + c.broker.slice(1))
    .join(', ')

  // When exactly one broker is expired, link directly to its reconnect modal.
  // When multiple are expired, link to the brokers page so the user can pick.
  const reconnectTo = expiredBrokers.length === 1
    ? `/brokers?reconnect=${encodeURIComponent(expiredBrokers[0].broker)}`
    : '/brokers'

  return (
    <div className="bg-amber-50 dark:bg-amber-950/40 border-b border-amber-200 dark:border-amber-800 px-4 py-2.5 flex items-center justify-between gap-3">
      <div className="flex items-center gap-2 min-w-0">
        <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-400 flex-shrink-0" />
        <p className="text-sm text-amber-700 dark:text-amber-300 truncate">
          <span className="font-medium">{names}</span>
          {' '}session{expiredBrokers.length > 1 ? 's have' : ' has'} expired — live trading is paused.
        </p>
      </div>
      <div className="flex items-center gap-3 flex-shrink-0">
        <Link
          to={reconnectTo}
          className="text-sm font-medium text-amber-700 dark:text-amber-300 hover:underline"
        >
          Reconnect now
        </Link>
        <button
          onClick={() => setDismissed(true)}
          className="text-amber-500 hover:text-amber-700 dark:text-amber-400 dark:hover:text-amber-200"
          aria-label="Dismiss"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  )
}

function EmailVerificationBanner() {
  const { resendVerification } = useAuth()
  const [status, setStatus] = useState(null)

  const handleResend = async () => {
    setStatus('sending')
    const result = await resendVerification()
    setStatus(result.success ? 'sent' : 'error')
  }

  return (
    <div className="bg-amber-50 dark:bg-amber-900/20 border-b border-amber-200 dark:border-amber-800 px-4 py-2.5 flex items-center justify-between">
      <p className="text-sm text-amber-700 dark:text-amber-400">
        Please verify your email address. Some features are restricted until
        verification.
      </p>
      <button
        onClick={handleResend}
        disabled={status === 'sending' || status === 'sent'}
        className="text-sm font-medium text-amber-700 dark:text-amber-400 hover:underline disabled:opacity-50 disabled:no-underline whitespace-nowrap ml-4"
      >
        {status === 'sending'
          ? 'Sending...'
          : status === 'sent'
            ? 'Email sent!'
            : 'Resend email'}
      </button>
    </div>
  )
}

export default DashboardLayout
